import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Capitalizes the first letter of a string while preserving the rest
 * @param str - The string to capitalize
 * @returns The string with first letter capitalized
 */
export function capitalize(str: string | undefined | null): string {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatDate(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInSeconds = diffInMs / 1000;
    const diffInMinutes = diffInSeconds / 60;
    const diffInHours = diffInMinutes / 60;
    const diffInDays = diffInHours / 24;

    if (diffInSeconds < 60) {
        return "just now";
    } else if (diffInMinutes < 60) {
        const mins = Math.floor(diffInMinutes);
        return `${mins}m ago`;
    } else if (diffInHours < 24) {
        const hours = Math.floor(diffInHours);
        return `${hours}h ago`;
    } else if (diffInDays < 7) {
        const days = Math.floor(diffInDays);
        return `${days}d ago`;
    } else {
        return date.toLocaleDateString();
    }
}

export function formatFullDate(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

export function formatTimeUntil(dateString: string): string {
    const now = new Date();
    const target = new Date(dateString);
    const diffMs = target.getTime() - now.getTime();

    if (diffMs <= 0) {
        return "soon";
    }

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    const parts: string[] = [];

    if (days > 0) {
        parts.push(`${days} day${days !== 1 ? "s" : ""}`);
    }

    if (hours > 0) {
        parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
    }

    if (minutes > 0) {
        parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
    }

    // If no parts, default to "less than a minute"
    if (parts.length === 0) {
        return "in less than a minute";
    }

    return `in ${parts.join(", ")}`;
}

export function getStatusBadge(status: string) {
    switch (status) {
        case "active":
            return "bg-info/10 text-info dark:bg-info/20 dark:text-info";
        case "finalized":
            return "bg-success/10 text-success dark:bg-success/20 dark:text-success";
        default:
            return "bg-muted text-muted-foreground";
    }
}

export function getStatusText(status: string) {
    switch (status) {
        case "active":
            return "In Progress";
        case "finalized":
            return "Completed";
        default:
            return "Paused";
    }
}
